import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useStageScheduleChecklist, type ChecklistItemMinimal, type LinkedChecklistItem, getMainStage, CONSTRUCTION_SUB_STAGE_ORDER } from '@/hooks/useStageScheduleChecklist';
import { useAuth } from '@/hooks/useAuth';
import { isChecklistItemComplete } from '@/lib/checklistProgress';
import { setupProjectChecklist } from '@/lib/projectChecklistSetup';
import { formatShortDate } from '@/lib/dateUtils';
import { jphDebugLog } from '@/lib/jphDebug';

interface Props {
  projectId: string;
  viewerRole?: 'management' | 'subcontractor' | 'client';
  onRequestAction?: (item: ChecklistItemMinimal) => void;
}

type StatusFilter = 'all' | 'unchecked' | 'checked' | 'waiting_management' | 'waiting_subcontractor' | 'fully_approved' | 'unmapped';

const STAGE_ORDER = ['Planning', 'Design', 'Consent', 'Construction', 'Handover'];

const categoryColors: Record<string, { bg: string; text: string }> = {
  general:       { bg: '#F9FAFB', text: '#6B7280' },
  structural:    { bg: '#FEF0E7', text: '#E8621A' },
  plumbing:      { bg: '#F0F9FF', text: '#0891B2' },
  electrical:    { bg: '#FFFBEB', text: '#F59E0B' },
  inspection:    { bg: '#F0FDF4', text: '#059669' },
  documentation: { bg: '#F5F3FF', text: '#7C3AED' },
  payment:       { bg: '#ECFDF5', text: '#059669' },
  handover:      { bg: '#FEF2F2', text: '#EF4444' },
};

export { isChecklistItemComplete };

/* Filter helper for any checklist item */
function matchesFilter(item: ChecklistItemMinimal, filter: StatusFilter): boolean {
  const needsMgmt = item.requires_management_check !== false;
  const needsSub = item.requires_subcontractor_check === true;
  const mgmtOk = !needsMgmt || item.management_checked;
  const subOk = !needsSub || item.subcontractor_checked;
  switch (filter) {
    case 'all': return true;
    case 'unchecked': return !mgmtOk || !subOk;
    case 'checked': return mgmtOk && subOk;
    case 'waiting_management': return needsMgmt && !item.management_checked;
    case 'waiting_subcontractor': return needsSub && !item.subcontractor_checked;
    case 'fully_approved': return mgmtOk && subOk;
    case 'unmapped': return true;
  }
  return true;
}

/* Filter helper for LinkedChecklistItem */
function linkedMatchesFilter(item: LinkedChecklistItem, filter: StatusFilter): boolean {
  const needsMgmt = item.requires_management_check !== false;
  const needsSub = item.requires_subcontractor_check === true;
  const mgmtOk = !needsMgmt || item.management_checked;
  const subOk = !needsSub || item.subcontractor_checked;
  switch (filter) {
    case 'all': return true;
    case 'unchecked': return !mgmtOk || !subOk;
    case 'checked': return mgmtOk && subOk;
    case 'waiting_management': return needsMgmt && !item.management_checked;
    case 'waiting_subcontractor': return needsSub && !item.subcontractor_checked;
    case 'fully_approved': return mgmtOk && subOk;
    case 'unmapped': return false;
  }
  return true;
}

export default function ChecklistTab({ projectId, viewerRole = 'management', onRequestAction }: Props) {
  const {
    stages,
    linkedMap,
    checklistItems,
    loading,
    error,
    mapping,
    togglingId,
    reload,
    unmappedChecklistItems,
    allScheduleItemsWithStage,
    checklistToSchedulesMap,
    checklistProgress,
    mapChecklistToSchedule,
    unmapChecklistFromSchedule,
    toggleChecklistItem,
  } = useStageScheduleChecklist(projectId);

  const { user } = useAuth();
  const { t } = useTranslation();
  const isManagement = viewerRole === 'management';

  const [stageFilter, setStageFilter] = useState<string>('all');
  const [subStageFilter, setSubStageFilter] = useState<string>('all');
  const [scheduleFilter, setScheduleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(viewerRole === 'subcontractor' ? 'waiting_subcontractor' : 'unchecked');
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set(STAGE_ORDER));
  const [expandedSubStages, setExpandedSubStages] = useState<Set<string>>(new Set());
  const [expandedScheduleItems, setExpandedScheduleItems] = useState<Set<string>>(new Set());
  const [mappingItemId, setMappingItemId] = useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grouped' | 'list'>('grouped');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  // Only show full spinner on the very first load when there is NO cached data.
  // If we have cached checklist items, render them immediately with a subtle refresh indicator.
  const hasData = checklistItems.length > 0;
  const [firstLoadDone, setFirstLoadDone] = useState(hasData);
  const prevLoadingRef = useRef(true);
  useEffect(() => {
    if (prevLoadingRef.current && !loading) setFirstLoadDone(true);
    prevLoadingRef.current = loading;
  }, [loading]);

  const isFirstLoad = loading && !firstLoadDone && !hasData;

  const [recoveryState, setRecoveryState] = useState<'idle' | 'loading' | 'success' | 'error' | 'exists'>('idle');
  const [recoveryMessage, setRecoveryMessage] = useState('');

  const handleCreateDefaultChecklist = useCallback(async () => {
    if (!user) return;
    setRecoveryState('loading');
    setRecoveryMessage('');

    // CRITICAL: project_id in checklist tables is TEXT — always use String(projectId)
    const projectIdText = String(projectId);

    jphDebugLog('Create default checklist clicked', {
      projectId: projectIdText,
      projectIdType: typeof projectIdText,
      userId: user.id,
    });

    try {
      const { data: projectRow, error: projErr } = await supabase
        .from('jphph_projects')
        .select('project_type')
        .eq('id', projectIdText)
        .maybeSingle();

      if (projErr) {
        // eslint-disable-next-line no-console
        console.error('[ChecklistTab] Failed to load project type:', projErr);
      }

      const projectType = (projectRow as { project_type?: string } | null)?.project_type || 'Renovation';

      jphDebugLog('Create default checklist clicked', {
        projectId: projectIdText,
        projectType,
      });

      const result = await setupProjectChecklist({
        projectId: projectIdText,
        projectType,
        createDefaultChecklist: true,
        userId: user.id,
        linkToSchedule: true,
        force: false, // CRITICAL FIX: do not force — let the edge function be idempotent
      });

      jphDebugLog('Default checklist creation result', {
        checklistRecordsCreated: result.checklistsCreated,
        checklistItemsCreated: result.itemsCreated,
        success: result.success,
        message: result.message,
        error: result.error || null,
        warning: result.warning || null,
      });

      // Reload and check what was actually inserted
      const { count: loadedChecklistCount } = await supabase
        .from('jphph_checklists')
        .select('id', { count: 'exact', head: true })
        .eq('project_id', projectIdText);
      const { count: loadedChecklistItemsCount, error: loadError } = await supabase
        .from('jphph_checklist_items')
        .select('id', { count: 'exact', head: true })
        .eq('project_id', projectIdText);

      jphDebugLog('Reload checklist after creation', {
        projectId: projectIdText,
        loadedChecklistCount: loadedChecklistCount ?? 0,
        loadedChecklistItemsCount: loadedChecklistItemsCount ?? 0,
        loadError: loadError?.message || null,
      });

      if (result.success && result.itemsCreated > 0) {
        setRecoveryState('success');
        setRecoveryMessage(t('checklist.defaultChecklistCreated', { count: result.itemsCreated }));
        reload();
      } else if (result.alreadyExists || result.message.includes('already exists')) {
        // Items already exist — force reload to show them
        setRecoveryState('exists');
        setRecoveryMessage(t('checklist.defaultChecklistExists'));
        reload(); // Always reload — items may exist but not be displayed
      } else {
        // PART 7: Show the ACTUAL error — never hide it
        setRecoveryState('error');
        const errorDetail = result.error || result.warning || result.message || t('checklist.failedToCreateDefault');
        setRecoveryMessage(`Checklist creation failed: ${errorDetail}`);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.error('[ChecklistTab] handleCreateDefaultChecklist exception:', errMsg);
      setRecoveryState('error');
      setRecoveryMessage(`Checklist creation failed: ${errMsg}`);
    }
  }, [projectId, user, t, reload]);

  const handleToggleCheck = useCallback(async (item: ChecklistItemMinimal) => {
    if (!user) return;
    await toggleChecklistItem(item, user.id, viewerRole);
  }, [user, toggleChecklistItem, viewerRole]);

  const handleToggleManagement = useCallback(async (item: ChecklistItemMinimal) => {
    if (!user) return;
    await toggleChecklistItem(item, user.id, 'management');
  }, [user, toggleChecklistItem]);

  const handleToggleSubcontractor = useCallback(async (item: ChecklistItemMinimal) => {
    if (!user) return;
    await toggleChecklistItem(item, user.id, 'subcontractor');
  }, [user, toggleChecklistItem]);

  const handleMap = useCallback(async (checklistItemId: string) => {
    if (!selectedScheduleId) return;
    const ok = await mapChecklistToSchedule(checklistItemId, selectedScheduleId);
    if (ok) {
      setMappingItemId(null);
      setSelectedScheduleId('');
    }
  }, [selectedScheduleId, mapChecklistToSchedule]);

  const filteredScheduleOptions = useMemo(() => {
    if (stageFilter === 'all') return allScheduleItemsWithStage;
    return allScheduleItemsWithStage.filter((s) => {
      const stage = getMainStage(s);
      return stage === stageFilter;
    });
  }, [allScheduleItemsWithStage, stageFilter]);

  const totalChecklistCount = checklistItems.length;
  const completedCount = checklistItems.filter((i) => isChecklistItemComplete(i)).length;
  const unmappedCount = unmappedChecklistItems.length;
  // Use shared single source of truth for all three-tier counts (identical to header)
  const {
    mgmtRequired,
    mgmtChecked,
    subRequired,
    subChecked,
    finalCompleted,
    finalTotal,
    overallPct,
    mgmtPct,
    subPct,
    subWaiting,
  } = checklistProgress;

  /* Derived: filtered unmapped items */
  const filteredUnmapped = useMemo(() => {
    if (statusFilter === 'unmapped') return unmappedChecklistItems;
    if (statusFilter === 'all') return unmappedChecklistItems;
    return unmappedChecklistItems.filter((i) => matchesFilter(i, statusFilter));
  }, [unmappedChecklistItems, statusFilter]);

  /* Helper: does a stage have any mapped items after filters? */
  const hasStageMappedItems = useCallback((stageName: string) => {
    const stage = stages.find((s) => s.name === stageName);
    if (!stage) return false;
    return stage.scheduleItems.some((si) => {
      if (scheduleFilter !== 'all' && scheduleFilter !== si.id) return false;
      const linked = linkedMap.get(si.id) || [];
      if (linked.length === 0) return false;
      if (statusFilter === 'all') return true;
      if (statusFilter === 'unmapped') return false;
      return linked.some((l) => linkedMatchesFilter(l, statusFilter));
    });
  }, [stages, linkedMap, scheduleFilter, statusFilter]);

  /* Stage-specific progress when a stage is selected */
  const stageProgress = useMemo(() => {
    if (stageFilter === 'all') return null;
    const stage = stages.find((s) => s.name === stageFilter);
    if (!stage) return null;
    const stageItems = checklistItems.filter((ci) => {
      const mappingsForItem = checklistToSchedulesMap.get(ci.id) || [];
      return mappingsForItem.some((m) => {
        const si = allScheduleItemsWithStage.find((s) => s.id === m.scheduleItemId);
        return si && getMainStage(si) === stageFilter;
      });
    });
    const stageMgmtRequired = stageItems.filter((i) => i.requires_management_check !== false).length;
    const stageMgmtChecked = stageItems.filter((i) => i.management_checked).length;
    const stageSubRequired = stageItems.filter((i) => i.requires_subcontractor_check === true).length;
    const stageSubChecked = stageItems.filter((i) => i.subcontractor_checked).length;
    const stageFinalCompleted = stageItems.filter((i) => isChecklistItemComplete(i)).length;
    const stageFinalTotal = stageItems.length;
    const stagePct = stageFinalTotal > 0 ? Math.round((stageFinalCompleted / stageFinalTotal) * 100) : 0;
    return { stageName: stageFilter, mgmtChecked: stageMgmtChecked, mgmtRequired: stageMgmtRequired, subChecked: stageSubChecked, subRequired: stageSubRequired, finalCompleted: stageFinalCompleted, finalTotal: stageFinalTotal, pct: stagePct };
  }, [stageFilter, stages, checklistItems, checklistToSchedulesMap, allScheduleItemsWithStage]);

  if (isFirstLoad) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-[#E8621A]">
            <i className="ri-loader-4-line text-3xl animate-spin"></i>
          </div>
          <p className="text-[#9CA3AF] text-sm">{t('checklist.loadingText')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#FEF2F2] border border-[#EF4444]/20 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#EF4444] flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-white text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[#1A1A1A] font-bold text-sm">{t('checklist.failedToLoadText')}</h3>
            <p className="text-[#6B7280] text-xs mt-1">{error}</p>
            <button onClick={reload} className="mt-3 inline-flex items-center gap-2 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap">
              <i className="ri-refresh-line"></i>{t('checklist.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (checklistItems.length === 0) {
    jphDebugLog('[ChecklistTab] Empty state — checklistItems.length === 0', {
      projectId,
      loading,
      error,
      firstLoadDone,
      isFirstLoad,
      stagesCount: stages.length,
      unmappedCount,
    });
    return (
      <div className="py-10 px-4">
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-6 text-center border-b border-gray-50">
            <div className="w-16 h-16 rounded-2xl bg-[#F8FAFC] border border-gray-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-list-check-2 text-3xl text-[#9CA3AF]"></i>
            </div>
            <h3 className="text-[#1A1A1A] font-bold text-base mb-2">{t('checklist.noChecklistItems')}</h3>
            <p className="text-[#6B7280] text-sm leading-relaxed max-w-xs mx-auto">
              {isManagement ? t('checklist.createChecklistDesc') : t('checklist.noChecklistItemsDesc')}
            </p>
          </div>

          {isManagement && (
            <div className="p-4 space-y-3">
              {/* Recovery states */}
              {recoveryState === 'success' && (
                <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#059669] flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-white text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[#059669] text-sm font-bold">{t('checklist.checklistCreatedSuccess')}</p>
                    <p className="text-[#059669]/80 text-xs mt-0.5">{recoveryMessage}</p>
                    <button onClick={() => { window.location.reload(); }} className="mt-2 text-[10px] text-[#059669] font-bold underline cursor-pointer">
                      {t('checklist.refreshPage')}
                    </button>
                  </div>
                </div>
              )}
              {recoveryState === 'exists' && (
                <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F59E0B] flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-white text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[#92400E] text-sm font-semibold">{recoveryMessage}</p>
                    <button
                      onClick={() => {
                        // Force clear cache and reload
                        window.location.reload();
                      }}
                      className="mt-2 text-[10px] text-[#E8621A] font-bold underline cursor-pointer"
                    >
                      {t('checklist.forceReload')}
                    </button>
                  </div>
                </div>
              )}
              {recoveryState === 'error' && (
                <div className="bg-[#FEF2F2] border border-[#EF4444]/20 rounded-xl p-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#EF4444] flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-white text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#EF4444] text-sm font-bold">{t('checklist.checklistCreationFailed')}</p>
                    <p className="text-[#EF4444]/80 text-xs mt-0.5 break-words">{recoveryMessage}</p>
                    <button
                      onClick={() => { setRecoveryState('idle'); setRecoveryMessage(''); }}
                      className="mt-2 text-[10px] text-[#EF4444] font-bold underline cursor-pointer"
                    >
                      {t('checklist.retry')}
                    </button>
                  </div>
                </div>
              )}

              {recoveryState !== 'success' && (
                <button
                  onClick={handleCreateDefaultChecklist}
                  disabled={recoveryState === 'loading'}
                  className="w-full flex items-center justify-center gap-2.5 bg-[#E8621A] text-white px-4 py-3.5 rounded-xl text-sm font-bold cursor-pointer disabled:opacity-60 hover:bg-[#B84C10] transition-colors leading-snug"
                  style={{ whiteSpace: 'normal', wordBreak: 'normal' }}
                >
                  {recoveryState === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base flex-shrink-0"></i>
                      {t('checklist.creatingDefaultChecklist')}
                    </>
                  ) : (
                    <>
                      <i className="ri-list-check-2 text-base flex-shrink-0"></i>
                      <span className="hidden sm:inline">{t('checklist.createDefaultChecklistForProject')}</span>
                      <span className="sm:hidden">{t('mobile.createDefaultChecklistForProject')}</span>
                    </>
                  )}
                </button>
              )}

              {/* Always offer force reload for management */}
              <button
                onClick={() => {
                  window.location.reload();
                }}
                className="w-full flex items-center justify-center gap-2 border border-gray-200 text-[#6B7280] px-4 py-2.5 rounded-xl text-xs font-medium cursor-pointer hover:bg-gray-50 transition-colors"
                style={{ whiteSpace: 'normal' }}
              >
                <i className="ri-refresh-line"></i>
                {t('checklist.forceReloadChecklist')}
              </button>

              <p className="text-[#9CA3AF] text-xs text-center">
                {t('checklist.checklistRecoveryNote')}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const isBackgroundRefresh = loading && firstLoadDone;

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">
      {/* Subtle background refresh indicator */}
      {isBackgroundRefresh && (
        <div className="bg-[#FEF0E7]/60 border border-[#FDBA74]/30 rounded-xl px-3 py-1.5 flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xs"></i>
          <span className="text-[#92400E] text-[11px] font-medium">{t('checklist.refreshingData')}</span>
        </div>
      )}
      {/* Three-tier progress header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        {/* Management Check */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[#1A1A1A] text-sm font-bold">{t('checklist.managementCheck')}</span>
            <span className={`text-sm font-bold ${mgmtPct === 100 ? 'text-[#059669]' : 'text-[#E8621A]'}`}>
              {mgmtChecked} / {mgmtRequired}
            </span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${mgmtPct}%`, backgroundColor: mgmtPct === 100 ? '#059669' : '#E8621A' }}
            ></div>
          </div>
        </div>

        {/* Subcontractor Check */}
        {subRequired > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[#1A1A1A] text-sm font-bold">{t('checklist.subcontractorCheck')}</span>
              <span className={`text-sm font-bold ${subPct === 100 ? 'text-[#0891B2]' : 'text-[#F59E0B]'}`}>
                {subChecked} / {subRequired}
                {subWaiting && <span className="ml-1.5 text-xs font-semibold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full">{t('checklist.waitingForSubChecks')}</span>}
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${subPct}%`, backgroundColor: subPct === 100 ? '#0891B2' : '#F59E0B' }}
              ></div>
            </div>
          </div>
        )}

        {/* Final Approval */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[#1A1A1A] text-sm font-bold">{t('checklist.finalApprovalLabel')}</span>
            <span className={`text-sm font-bold ${overallPct === 100 ? 'text-[#059669]' : 'text-[#E8621A]'}`}>
              {finalCompleted} / {finalTotal} {overallPct}%
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${overallPct}%`, backgroundColor: overallPct === 100 ? '#059669' : '#E8621A' }}
            ></div>
          </div>
          <p className="text-[10px] text-[#9CA3AF]">
            {t('checklist.finalApprovalComplete')}
          </p>
        </div>

        {unmappedCount > 0 && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[10px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-2 py-1 rounded-full">
              {t('checklist.unmappedItemsCount', { count: unmappedCount })}
            </span>
          </div>
        )}
      </div>

      {/* View mode toggle */}
      <div className="flex bg-gray-100 rounded-xl p-1">
        <button
          onClick={() => setViewMode('grouped')}
          className={`flex-1 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-normal sm:whitespace-nowrap transition-colors ${
            viewMode === 'grouped' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#9CA3AF]'
          }`}
        >
          <i className="ri-list-check mr-1"></i>{t('checklist.byStageOption')}
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={`flex-1 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-normal sm:whitespace-nowrap transition-colors ${
            viewMode === 'list' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#9CA3AF]'
          }`}
        >
          <i className="ri-file-list-line mr-1"></i>{t('checklist.bySectionOption')}
        </button>
      </div>

      {/* Filters - only show in grouped mode */}
      {viewMode === 'grouped' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-3 space-y-2">
          <div className="flex flex-col gap-2">
            <select
              value={stageFilter}
              onChange={(e) => { setStageFilter(e.target.value); setSubStageFilter('all'); setScheduleFilter('all'); }}
              className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#374151] cursor-pointer"
            >
              <option value="all">{t('checklist.allStagesOption')}</option>
              {STAGE_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {stageFilter === 'Construction' && (
              <select
                value={subStageFilter}
                onChange={(e) => { setSubStageFilter(e.target.value); setScheduleFilter('all'); }}
                className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#374151] cursor-pointer"
              >
                <option value="all">{t('checklist.allConstructionSubStages')}</option>
                {CONSTRUCTION_SUB_STAGE_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <select
              value={scheduleFilter}
              onChange={(e) => setScheduleFilter(e.target.value)}
              className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#374151] cursor-pointer"
            >
              <option value="all">{t('checklist.allScheduleItemsOption')}</option>
              {filteredScheduleOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.task_no ?? ''} {s.title}</option>
              ))}
            </select>
            <div className="w-full overflow-x-auto scrollbar-hide -mx-0.5 px-0.5">
              <div className="flex flex-nowrap gap-1.5 min-w-max">
              {([
                { key: 'all', label: t('checklist.filterAll') },
                { key: 'unchecked', label: t('checklist.filterUnchecked') },
                { key: 'checked', label: t('checklist.filterChecked') },
                ...(isManagement ? [
                  { key: 'waiting_management', label: t('checklist.filterWaitMgmt') },
                  { key: 'waiting_subcontractor', label: t('checklist.filterWaitSub') },
                  { key: 'fully_approved', label: t('checklist.filterFullyApproved') },
                ] : viewerRole === 'subcontractor' ? [
                  { key: 'waiting_subcontractor', label: t('checklist.filterWaitSub') },
                  { key: 'fully_approved', label: t('checklist.filterFullyApproved') },
                ] : []),
                { key: 'unmapped', label: t('checklist.filterUnmapped') },
              ] as { key: StatusFilter; label: string }[]).map((f) => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors min-h-[36px] ${
                    statusFilter === f.key ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stage-specific three-tier progress header */}
      {stageProgress && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[#1A1A1A] text-sm font-bold">{t('checklist.stageProgress', { stage: stageProgress.stageName })}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-[#F0FDF4] rounded-xl p-2 text-center border border-[#BBF7D0]">
              <p className="text-[10px] font-semibold text-[#059669] uppercase tracking-wider mb-0.5">{t('checklist.mgmtLabel')}</p>
              <p className="text-sm font-bold text-[#059669]">{stageProgress.mgmtChecked} / {stageProgress.mgmtRequired}</p>
            </div>
            {stageProgress.subRequired > 0 && (
              <div className="bg-[#ECFEFF] rounded-xl p-2 text-center border border-[#A5F3FC]">
                <p className="text-[10px] font-semibold text-[#0891B2] uppercase tracking-wider mb-0.5">{t('checklist.subLabel')}</p>
                <p className="text-sm font-bold text-[#0891B2]">{stageProgress.subChecked} / {stageProgress.subRequired}</p>
              </div>
            )}
            <div className="bg-[#FEF0E7] rounded-xl p-2 text-center border border-[#FDBA74]">
              <p className="text-[10px] font-semibold text-[#92400E] uppercase tracking-wider mb-0.5">{t('checklist.finalLabel')}</p>
              <p className="text-sm font-bold text-[#92400E]">{stageProgress.finalCompleted} / {stageProgress.finalTotal} <span className="text-xs">({stageProgress.pct}%)</span></p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${stageProgress.pct}%`, backgroundColor: stageProgress.pct === 100 ? '#059669' : '#E8621A' }}
            ></div>
          </div>
        </div>
      )}

      {viewMode === 'grouped' ? (
        <>
          {/* Mapped checklist items grouped by Stage > Sub-Stage > Schedule Item */}
          {stages.map((stage) => {
            if (stageFilter !== 'all' && stageFilter !== stage.name) return null;
            const isStageExpanded = expandedStages.has(stage.name);

            // For Construction with sub-stage grouping
            if (stage.name === 'Construction' && stage.constructionSubStages && stage.constructionSubStages.length > 0) {
              const activeSubStages = stage.constructionSubStages.filter((sub) => {
                if (subStageFilter !== 'all' && subStageFilter !== sub.name) return false;
              const subSchedules = sub.scheduleItems
                  .filter((si) => scheduleFilter === 'all' || scheduleFilter === si.id)
                  .map((si) => {
                    const linked = linkedMap.get(si.id) || [];
                    const filtered = linked.filter((item) => {
                      if (statusFilter === 'all') return true;
                      if (statusFilter === 'unmapped') return false;
                      return linkedMatchesFilter(item, statusFilter);
                    });
                    return { schedule: si, items: filtered };
                  })
                  .filter((g) => g.items.length > 0);
                return subSchedules.length > 0;
              });

              if (activeSubStages.length === 0) return null;

              return (
                <div key={stage.name} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => setExpandedStages((prev) => {
                      const next = new Set(prev);
                      if (next.has(stage.name)) next.delete(stage.name);
                      else next.add(stage.name);
                      return next;
                    })}
                    className="w-full text-left px-4 py-3.5 cursor-pointer flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: stage.bgColor }}>
                      <i className={`${stage.icon} text-lg`} style={{ color: stage.accentColor }}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-bold text-[#1A1A1A]">{stage.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[10px] text-[#9CA3AF]">{stage.checklistCount} {t('checklist.checksCount')}</span>
                          <i className={`${isStageExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF] text-lg`}></i>
                        </div>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
                        <div className="h-full rounded-full transition-all" style={{ width: `${stage.checklistCompletionPct}%`, backgroundColor: stage.accentColor }}></div>
                      </div>
                    </div>
                  </button>

                  {isStageExpanded && (
                    <div className="border-t border-gray-50">
                      {activeSubStages.map((sub) => {
                        const subSchedules = sub.scheduleItems
                          .filter((si) => scheduleFilter === 'all' || scheduleFilter === si.id)
                          .map((si) => {
                            const linked = linkedMap.get(si.id) || [];
                            const filtered = linked.filter((item) => {
                              if (statusFilter === 'all') return true;
                              if (statusFilter === 'unmapped') return false;
                              return linkedMatchesFilter(item, statusFilter);
                            });
                            return { schedule: si, items: filtered };
                          })
                          .filter((g) => g.items.length > 0);

                        const isSubExpanded = expandedSubStages.has(sub.name);
                        return (
                          <div key={sub.name} className="border-b border-gray-50 last:border-b-0">
                            <button
                              onClick={() => setExpandedSubStages((prev) => {
                                const next = new Set(prev);
                                if (next.has(sub.name)) next.delete(sub.name);
                                else next.add(sub.name);
                                return next;
                              })}
                              className="w-full text-left px-4 py-2.5 cursor-pointer flex items-center gap-2 bg-gray-50/50"
                            >
                              <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: sub.bgColor }}>
                                <i className={`${sub.icon} text-xs`} style={{ color: sub.accentColor }}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-bold text-[#374151]">{sub.name}</span>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <span className="text-[9px] font-bold text-[#9CA3AF]">{t('checklistSub.checksCount', { completed: sub.completedChecklistCount, total: sub.checklistCount })}</span>
                                    <span className="text-[9px] font-bold text-[#0891B2]">{t('checklistSub.paymentPercent', { n: sub.paymentPercent })}</span>
                                    <i className={`${isSubExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF] text-sm`}></i>
                                  </div>
                                </div>
                                <div className="h-1 bg-gray-100 rounded-full overflow-hidden mt-1">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${sub.checklistCompletionPct}%`, backgroundColor: sub.accentColor }}></div>
                                </div>
                              </div>
                            </button>

                            {isSubExpanded && (
                              <div>
                                {subSchedules.map((g) => {
                                  const isSchedExpanded = expandedScheduleItems.has(g.schedule.id);
                                  return (
                                    <div key={g.schedule.id} className="border-b border-gray-50 last:border-b-0">
                                      <button
                                        onClick={() => setExpandedScheduleItems((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(g.schedule.id)) next.delete(g.schedule.id);
                                          else next.add(g.schedule.id);
                                          return next;
                                        })}
                                        className="w-full text-left px-4 py-2 cursor-pointer flex items-center gap-2"
                                      >
                                        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold" style={{ backgroundColor: g.schedule.is_complete ? '#F0FDF4' : '#F9FAFB', color: g.schedule.is_complete ? '#059669' : '#9CA3AF' }}>
                                          {g.schedule.is_complete ? <i className="ri-check-line text-[9px]"></i> : g.schedule.task_no ?? ''}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between gap-2">
                                            <p className={`text-sm font-medium truncate ${g.schedule.is_complete ? 'text-[#9CA3AF] line-through' : 'text-[#374151]'}`}>
                                              {g.schedule.title}
                                            </p>
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                              <span className="text-[10px] text-[#9CA3AF]">{g.items.length} {t('checklist.checksCount')}</span>
                                              <i className={`${isSchedExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF] text-sm`}></i>
                                            </div>
                                          </div>
                                        </div>
                                      </button>

                                      {isSchedExpanded && (
                                        <div className="px-4 pb-2 space-y-1.5">
                                          {g.items.map((item) => (
                                            <ChecklistRow
                                              key={item.id}
                                              item={item}
                                              isToggling={togglingId === item.id}
                                              viewerRole={viewerRole}
                                              onToggleManagement={() => {
                                                const ci = checklistItems.find((c) => c.id === item.id);
                                                if (ci) handleToggleManagement(ci);
                                              }}
                                              onToggleSubcontractor={() => {
                                                const ci = checklistItems.find((c) => c.id === item.id);
                                                if (ci) handleToggleSubcontractor(ci);
                                              }}
                                              onUnlink={isManagement && item.mappingId ? () => unmapChecklistFromSchedule(item.mappingId!) : undefined}
                                              onRequestAction={onRequestAction ? (li) => {
                                                const ci = checklistItems.find((c) => c.id === li.id);
                                                if (ci) onRequestAction(ci);
                                              } : undefined}
                                            />
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Non-construction stages (or Construction without sub-stages)
            const scheduleWithItems = stage.scheduleItems
              .filter((si) => scheduleFilter === 'all' || scheduleFilter === si.id)
              .map((si) => {
                const linked = linkedMap.get(si.id) || [];
                const filtered = linked.filter((item) => {
                  if (statusFilter === 'all') return true;
                  if (statusFilter === 'unmapped') return false;
                  return linkedMatchesFilter(item, statusFilter);
                });
                return { schedule: si, items: filtered };
              })
              .filter((g) => g.items.length > 0);

            if (scheduleWithItems.length === 0) return null;

            return (
              <div key={stage.name} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setExpandedStages((prev) => {
                    const next = new Set(prev);
                    if (next.has(stage.name)) next.delete(stage.name);
                    else next.add(stage.name);
                    return next;
                  })}
                  className="w-full text-left px-4 py-3.5 cursor-pointer flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: stage.bgColor }}>
                    <i className={`${stage.icon} text-lg`} style={{ color: stage.accentColor }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-bold text-[#1A1A1A]">{stage.name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] text-[#9CA3AF]">{stage.checklistCount} {t('checklist.checksCount')}</span>
                        <i className={`${isStageExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF] text-lg`}></i>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
                      <div className="h-full rounded-full transition-all" style={{ width: `${stage.checklistCompletionPct}%`, backgroundColor: stage.accentColor }}></div>
                    </div>
                  </div>
                </button>

                {isStageExpanded && (
                  <div className="border-t border-gray-50">
                    {scheduleWithItems.map((g) => {
                      const isSchedExpanded = expandedScheduleItems.has(g.schedule.id);
                      return (
                        <div key={g.schedule.id} className="border-b border-gray-50 last:border-b-0">
                          <button
                            onClick={() => setExpandedScheduleItems((prev) => {
                              const next = new Set(prev);
                              if (next.has(g.schedule.id)) next.delete(g.schedule.id);
                              else next.add(g.schedule.id);
                              return next;
                            })}
                            className="w-full text-left px-4 py-2.5 cursor-pointer flex items-center gap-2"
                          >
                            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold" style={{ backgroundColor: g.schedule.is_complete ? '#F0FDF4' : '#F9FAFB', color: g.schedule.is_complete ? '#059669' : '#9CA3AF' }}>
                              {g.schedule.is_complete ? <i className="ri-check-line text-[10px]"></i> : g.schedule.task_no ?? ''}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-sm font-medium truncate ${g.schedule.is_complete ? 'text-[#9CA3AF] line-through' : 'text-[#374151]'}`}>
                                  {g.schedule.title}
                                </p>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className="text-[10px] text-[#9CA3AF]">{g.items.length} {t('checklist.checksCount')}</span>
                                  <i className={`${isSchedExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF] text-sm`}></i>
                                </div>
                              </div>
                              {g.schedule.start_date && g.schedule.end_date && (
                                <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                                  {formatShortDate(g.schedule.start_date)} — {formatShortDate(g.schedule.end_date)}
                                </p>
                              )}
                            </div>
                          </button>

                          {isSchedExpanded && (
                            <div className="px-4 pb-3 space-y-1.5">
                              {g.items.map((item) => (
                                <ChecklistRow
                                  key={item.id}
                                  item={item}
                                  isToggling={togglingId === item.id}
                                  viewerRole={viewerRole}
                                  onToggleManagement={() => {
                                    const ci = checklistItems.find((c) => c.id === item.id);
                                    if (ci) handleToggleManagement(ci);
                                  }}
                                  onToggleSubcontractor={() => {
                                    const ci = checklistItems.find((c) => c.id === item.id);
                                    if (ci) handleToggleSubcontractor(ci);
                                  }}
                                  onUnlink={isManagement && item.mappingId ? () => unmapChecklistFromSchedule(item.mappingId!) : undefined}
                                  onRequestAction={onRequestAction ? (li) => {
                                    const ci = checklistItems.find((c) => c.id === li.id);
                                    if (ci) onRequestAction(ci);
                                  } : undefined}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Empty state for selected stage with no mapped items */}
          {stageFilter !== 'all' && !hasStageMappedItems(stageFilter) && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-[#F9FAFB] flex items-center justify-center mx-auto mb-3">
                <i className="ri-folder-3-line text-xl text-[#D1D5DB]"></i>
              </div>
              <h3 className="text-[#1A1A1A] font-bold text-sm mb-1">
                {t('checklist.noMappedItems', { stage: stageFilter })}
              </h3>
              <p className="text-[#9CA3AF] text-xs mb-3">
                {t('checklist.noMappedDesc')}
              </p>
              {unmappedCount > 0 && (
                <div className="inline-flex items-center gap-2 bg-[#FFFBEB] border border-[#F59E0B]/20 rounded-xl px-3 py-2">
                  <i className="ri-error-warning-line text-[#F59E0B] text-sm"></i>
                  <span className="text-xs text-[#92400E] font-medium">{unmappedCount} {t('checklist.unmappedItemsCount', { count: '' }).trim()}</span>
                </div>
              )}
            </div>
          )}

          {/* Unmapped checklist items — always visible in grouped mode */}
          {filteredUnmapped.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#F59E0B]/20 overflow-hidden">
              <div className="px-4 py-3.5 border-b border-[#F59E0B]/10 bg-[#FFFBEB]/50">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-line text-[#F59E0B] text-lg"></i>
                    <span className="text-sm font-bold text-[#92400E]">{t('checklist.unmappedItemsTitle')}</span>
                    <span className="text-[10px] font-bold text-[#F59E0B] bg-white px-2 py-0.5 rounded-full">{filteredUnmapped.length}</span>
                  </div>
                </div>
                <p className="text-[10px] text-[#9CA3AF] mt-1">
                  {t('checklist.unmappedItemsDesc')}
                </p>
              </div>
              <div className="px-4 py-3 space-y-2">
                {filteredUnmapped.map((item) => (
                  <div key={item.id} className="w-full min-w-0 overflow-hidden py-2 transition-opacity duration-300">
                    {/* Top row: checkbox + text */}
                    <div className="flex items-start gap-3">
                    <button
                      onClick={() => {
                        if (isManagement) handleToggleCheck(item);
                      }}
                      disabled={togglingId === item.id}
                      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors cursor-pointer disabled:opacity-50 mt-0.5 ${
                        isChecklistItemComplete(item)
                          ? 'bg-[#059669] border-[#059669]'
                          : isManagement
                            ? 'bg-white border-gray-300 hover:border-[#E8621A]'
                            : 'bg-white border-gray-200'
                      }`}
                    >
                      {togglingId === item.id ? (
                        <i className="ri-loader-4-line animate-spin text-white text-xs"></i>
                      ) : isChecklistItemComplete(item) ? (
                        <i className="ri-check-line text-white text-xs"></i>
                      ) : (
                        <span className="text-[10px] font-bold text-[#9CA3AF]">&nbsp;</span>
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isChecklistItemComplete(item) ? 'text-[#9CA3AF] line-through' : 'text-[#374151]'}`}>
                        {item.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-[#9CA3AF] capitalize">{item.category}</span>
                        {item.checklist_title && <span className="text-[10px] text-[#9CA3AF]">{item.checklist_title}</span>}
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${isChecklistItemComplete(item) ? 'bg-[#F0FDF4] text-[#059669]' : 'bg-[#FEF0E7] text-[#E8621A]'}`}>
                          {isChecklistItemComplete(item) ? t('checklist.completedLabel') : t('checklist.requiredLabel')}
                        </span>
                      </div>
                    </div>
                    {isManagement && mappingItemId !== item.id && (
                      <button
                        onClick={() => { setMappingItemId(item.id); setSelectedScheduleId(''); }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#E8621A]/20 text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap hover:bg-[#FEF0E7] transition-colors flex-shrink-0"
                      >
                        <i className="ri-add-circle-line"></i>{t('checklist.mapBtn')}
                      </button>
                    )}
                    </div>
                    {/* Mapping controls — full width below the top row */}
                    {isManagement && mappingItemId === item.id && (
                      <div className="flex flex-col gap-2 w-full min-w-0 mt-2">
                        <select
                          value={selectedScheduleId}
                          onChange={(e) => setSelectedScheduleId(e.target.value)}
                          className="w-full min-w-0 max-w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs text-[#374151] cursor-pointer"
                          style={{ boxSizing: 'border-box' }}
                        >
                          <option value="">{t('checklist.selectSchedule')}</option>
                          {allScheduleItemsWithStage.map((s) => (
                            <option key={s.id} value={s.id}>
                              [{getMainStage(s)}] {s.task_no ?? ''} {s.title}
                            </option>
                          ))}
                        </select>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleMap(item.id)}
                            disabled={!selectedScheduleId || mapping}
                            className="flex-1 px-2.5 py-1.5 rounded-lg bg-[#E8621A] text-white text-xs font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap disabled:opacity-50"
                          >
                            {mapping ? <i className="ri-loader-4-line animate-spin"></i> : t('checklist.mapBtn')}
                          </button>
                          <button
                            onClick={() => { setMappingItemId(null); setSelectedScheduleId(''); }}
                            className="flex-1 px-2.5 py-1.5 rounded-lg bg-gray-100 text-[#374151] text-xs font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap"
                          >
                            {t('checklist.cancelBtn')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Friendly empty states */}
          {(() => {
            // All items checked + unchecked filter selected
            const allChecked = checklistItems.length > 0 && checklistItems.every((i) => isChecklistItemComplete(i));
            const isFilteredEmpty = stages.every((s) => {
              if (stageFilter !== 'all' && stageFilter !== s.name) return true;
              const items = s.scheduleItems.filter((si) => scheduleFilter === 'all' || scheduleFilter === si.id);
              return items.length === 0 || items.every((si) => (linkedMap.get(si.id) || []).length === 0);
            }) && filteredUnmapped.length === 0;

            if (allChecked && statusFilter === 'unchecked' && isFilteredEmpty) {
              return (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-xl bg-[#F0FDF4] flex items-center justify-center mx-auto mb-3">
                    <i className="ri-checkbox-circle-line text-xl text-[#059669]"></i>
                  </div>
                  <p className="text-[#059669] text-sm font-bold">{t('checklist.allItemsChecked')}</p>
                  <button
                    onClick={() => setStatusFilter('checked')}
                    className="mt-2 text-[#0891B2] text-xs font-semibold cursor-pointer hover:underline"
                  >
                    {t('checklist.viewCheckedItems')}
                  </button>
                </div>
              );
            }

            if (isFilteredEmpty) {
              return (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-xl bg-[#F9FAFB] flex items-center justify-center mx-auto mb-3">
                    <i className="ri-search-line text-xl text-[#D1D5DB]"></i>
                  </div>
                  <p className="text-[#9CA3AF] text-sm">{t('checklist.noItemsMatchFilters')}</p>
                  {statusFilter !== 'all' && (
                    <button
                      onClick={() => setStatusFilter('all')}
                      className="mt-2 text-[#E8621A] text-xs font-semibold cursor-pointer hover:underline"
                    >
                      {t('checklist.showAllItems')}
                    </button>
                  )}
                </div>
              );
            }

            return null;
          })()}
        </>
      ) : (
        /* By Section (grouped by template section / category) */
        <div className="space-y-4">
          {/* Status filter bar for By Section mode */}
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex flex-nowrap gap-1.5 min-w-max">
            {([
              { key: 'all', label: t('checklist.filterAll') },
              { key: 'unchecked', label: t('checklist.filterUnchecked') },
              { key: 'checked', label: t('checklist.filterChecked') },
              ...(isManagement ? [
                { key: 'waiting_management', label: t('checklist.filterWaitMgmt') },
                { key: 'waiting_subcontractor', label: t('checklist.filterWaitSub') },
                { key: 'fully_approved', label: t('checklist.filterFullyApproved') },
              ] : viewerRole === 'subcontractor' ? [
                { key: 'waiting_subcontractor', label: t('checklist.filterWaitSub') },
                { key: 'fully_approved', label: t('checklist.filterFullyApproved') },
              ] : []),
            ] as { key: StatusFilter; label: string }[]).map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors min-h-[36px] ${
                  statusFilter === f.key ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
                }`}
              >
                {f.label}
              </button>
            ))}
            </div>
          </div>

          {(() => {
            // Filter items by status
            let filtered = checklistItems;
            if (statusFilter === 'all') filtered = checklistItems;
            else if (statusFilter === 'unmapped') filtered = [];
            else filtered = checklistItems.filter((i) => matchesFilter(i, statusFilter));

            // Group by source_section_title first, fallback to category
            const bySection = new Map<string, ChecklistItemMinimal[]>();
            for (const item of filtered) {
              const section = item.source_section_title || item.category || 'General';
              const arr = bySection.get(section) || [];
              arr.push(item);
              bySection.set(section, arr);
            }

            const sortedSections = Array.from(bySection.keys()).sort();

            if (sortedSections.length === 0) {
              const allChecked = checklistItems.length > 0 && checklistItems.every((i) => isChecklistItemComplete(i));
              if (allChecked && statusFilter === 'unchecked') {
                return (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 rounded-xl bg-[#F0FDF4] flex items-center justify-center mx-auto mb-3">
                      <i className="ri-checkbox-circle-line text-xl text-[#059669]"></i>
                    </div>
                    <p className="text-[#059669] text-sm font-bold">{t('checklist.allItemsCheckedGreat')}</p>
                    <button
                      onClick={() => setStatusFilter('checked')}
                      className="mt-2 text-[#0891B2] text-xs font-semibold cursor-pointer hover:underline"
                    >
                      {t('checklist.viewCheckedItemsBtn')}
                    </button>
                  </div>
                );
              }
              return (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-xl bg-[#F9FAFB] flex items-center justify-center mx-auto mb-3">
                    <i className="ri-search-line text-xl text-[#D1D5DB]"></i>
                  </div>
                  <p className="text-[#9CA3AF] text-sm">{t('checklist.noItemsMatchFilter')}</p>
                  {statusFilter !== 'all' && (
                    <button
                      onClick={() => setStatusFilter('all')}
                      className="mt-2 text-[#E8621A] text-xs font-semibold cursor-pointer hover:underline"
                    >
                      {t('checklist.showAllItems')}
                    </button>
                  )}
                </div>
              );
            }

            return sortedSections.map((section) => {
              const sectionItems = bySection.get(section)!;
              const sectionCompleted = sectionItems.filter((i) => isChecklistItemComplete(i)).length;
              const sectionPct = sectionItems.length > 0 ? Math.round((sectionCompleted / sectionItems.length) * 100) : 0;
              const catKey = sectionItems[0]?.category || 'general';
              const catCfg = categoryColors[catKey] || categoryColors.general;

              return (
                <div key={section} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: catCfg.bg }}>
                      <i className="ri-folder-3-line text-sm" style={{ color: catCfg.text }}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-bold text-[#1A1A1A]">{section}</span>
                        <span className="text-[10px] text-[#9CA3AF]">{sectionCompleted}/{sectionItems.length}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1.5">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${sectionPct}%`, backgroundColor: catCfg.text }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-50 px-4 py-3 space-y-2">
                    {sectionItems.map((item, idx) => {
                      const isComplete = isChecklistItemComplete(item);
                      const needsMgmt = item.requires_management_check !== false;
                      const needsSub = item.requires_subcontractor_check === true;

                      const mappingsForItem = checklistToSchedulesMap.get(item.id) || [];
                      const firstMapping = mappingsForItem[0];
                      const si = firstMapping ? allScheduleItemsWithStage.find((s) => s.id === firstMapping.scheduleItemId) : undefined;

                      return (
                        <div
                          key={item.id}
                          className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 border transition-colors ${
                            isComplete
                              ? 'border-[#059669]/20 bg-[#F0FDF4]/40'
                              : 'border-gray-100 bg-[#F9FAFB]/60'
                          }`}
                        >
                          {/* Status icon */}
                          <div className="flex flex-col gap-1 flex-shrink-0 mt-0.5">
                            {needsMgmt && (
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${item.management_checked ? 'bg-[#059669] border-[#059669]' : 'bg-white border-gray-200'}`}>
                                {item.management_checked ? <i className="ri-check-line text-white text-[8px]"></i> : <span className="text-[8px] font-bold text-[#9CA3AF]">M</span>}
                              </div>
                            )}
                            {needsSub && (
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${item.subcontractor_checked ? 'bg-[#0891B2] border-[#0891B2]' : 'bg-white border-gray-200'}`}>
                                {item.subcontractor_checked ? <i className="ri-check-line text-white text-[8px]"></i> : <span className="text-[8px] font-bold text-[#9CA3AF]">S</span>}
                              </div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium leading-snug ${isComplete ? 'text-[#9CA3AF] line-through' : 'text-[#374151]'}`}>
                              {item.title}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: catCfg.bg, color: catCfg.text }}>
                                {item.category}
                              </span>
                              {/* Separate status badges: Mgmt / Sub / Final */}
                              {needsMgmt && (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${item.management_checked ? 'bg-[#F0FDF4] text-[#059669]' : 'bg-[#FEF0E7] text-[#E8621A]'}`}>
                                  {t('progress.management')}: {item.management_checked ? t('progress.done') : t('progress.pending')}
                                </span>
                              )}
                              {needsMgmt && !needsSub && item.management_checked && (
                                <span className="text-[9px] font-bold bg-[#F0FDF4] text-[#059669] px-1.5 py-0.5 rounded-full">{t('checklist.finalCompleteLabel')}</span>
                              )}
                              {needsSub && (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${item.subcontractor_checked ? 'bg-[#ECFEFF] text-[#0891B2]' : 'bg-[#FFFBEB] text-[#F59E0B]'}`}>
                                  {t('progress.subcontractor')}: {item.subcontractor_checked ? t('progress.done') : t('progress.pending')}
                                </span>
                              )}
                              {needsSub && item.management_checked && !item.subcontractor_checked && (
                                <span className="text-[9px] font-bold bg-[#FFFBEB] text-[#F59E0B] px-1.5 py-0.5 rounded-full">{t('checklist.waitingForSubLabel')}</span>
                              )}
                              {needsSub && !item.management_checked && (
                                <span className="text-[9px] font-bold bg-[#FEF0E7] text-[#E8621A] px-1.5 py-0.5 rounded-full">{t('checklist.waitingForMgmtLabel')}</span>
                              )}
                              {isComplete && (needsMgmt || needsSub) && (
                                <span className="text-[9px] font-bold bg-[#F0FDF4] text-[#059669] px-1.5 py-0.5 rounded-full">{t('checklist.finalCompleteLabel')}</span>
                              )}
                              {!needsMgmt && !needsSub && (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${isComplete ? 'bg-[#F0FDF4] text-[#059669]' : 'bg-[#FEF0E7] text-[#E8621A]'}`}>
                                  {isComplete ? t('checklist.completedLabel') : t('checklist.requiredLabel')}
                                </span>
                              )}
                              {si && (
                                <span className="text-[10px] font-medium text-[#0891B2] bg-[#F0F9FF] px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
                                  <i className="ri-link-m text-[9px]"></i>
                                  {getMainStage(si)}
                                  {si.subStage && ` / ${si.subStage}`}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            {needsMgmt && (
                              <button
                                onClick={() => { if (isManagement && togglingId !== item.id) handleToggleManagement(item); }}
                                disabled={!isManagement || togglingId === item.id}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap border-2 transition-colors disabled:opacity-40 ${
                                  item.management_checked
                                    ? 'bg-[#F0FDF4] border-[#BBF7D0] text-[#059669]'
                                    : 'bg-white border-[#E8621A] text-[#E8621A] hover:bg-[#FEF0E7]'
                                }`}
                                title={t('checklist.managementCheck')}
                              >
                                {togglingId === item.id ? (
                                  <i className="ri-loader-4-line animate-spin text-[8px]"></i>
                                ) : item.management_checked ? (
                                  <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[8px]"></i> {t('checklist.managementDoneShort')}</span>
                                ) : (
                                  <span className="flex items-center gap-1"><i className="ri-checkbox-blank-circle-line text-[8px]"></i> {t('checklist.completeMgmtShort')}</span>
                                )}
                              </button>
                            )}
                            {needsSub && (
                              <button
                                onClick={() => { if ((isManagement || viewerRole === 'subcontractor') && togglingId !== item.id) handleToggleSubcontractor(item); }}
                                disabled={togglingId === item.id || !(isManagement || viewerRole === 'subcontractor')}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap border-2 transition-colors disabled:opacity-40 ${
                                  item.subcontractor_checked
                                    ? 'bg-[#ECFEFF] border-[#A5F3FC] text-[#0891B2]'
                                    : 'bg-white border-[#0891B2] text-[#0891B2] hover:bg-[#ECFEFF]'
                                }`}
                                title={t('checklist.subcontractorCheck')}
                              >
                                {togglingId === item.id ? (
                                  <i className="ri-loader-4-line animate-spin text-[8px]"></i>
                                ) : item.subcontractor_checked ? (
                                  <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[8px]"></i> {t('checklist.subDoneShort')}</span>
                                ) : (
                                  <span className="flex items-center gap-1"><i className="ri-checkbox-blank-circle-line text-[8px]"></i> {t('checklist.completeSubShort')}</span>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}

/* Inline Checklist row with dual check indicators */
interface ChecklistRowProps {
  item: LinkedChecklistItem;
  isToggling: boolean;
  viewerRole: 'management' | 'subcontractor' | 'client';
  onToggleManagement: () => void;
  onToggleSubcontractor: () => void;
  onUnlink?: () => void;
  onRequestAction?: (item: LinkedChecklistItem) => void;
}

function ChecklistRow({ item, isToggling, viewerRole, onToggleManagement, onToggleSubcontractor, onUnlink, onRequestAction }: ChecklistRowProps) {
  const { t } = useTranslation();
  const catCfg = categoryColors[item.category] || categoryColors.general;
  const isManagement = viewerRole === 'management';
  const isSubcontractor = viewerRole === 'subcontractor';

  const needsMgmt = item.requires_management_check !== false;
  const needsSub = item.requires_subcontractor_check === true;
  const isFullyComplete = item.is_completed;

  return (
    <div
      className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 border transition-colors ${
        isFullyComplete
          ? 'border-[#059669]/20 bg-[#F0FDF4]/40'
          : 'border-gray-100 bg-[#F9FAFB]/60'
      }`}
    >
      {/* Status icon */}
      <div className="flex flex-col gap-1 flex-shrink-0 mt-0.5">
        {needsMgmt && (
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${item.management_checked ? 'bg-[#059669] border-[#059669]' : 'bg-white border-gray-200'}`}>
            {item.management_checked ? <i className="ri-check-line text-white text-[8px]"></i> : <span className="text-[8px] font-bold text-[#9CA3AF]">M</span>}
          </div>
        )}
        {needsSub && (
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${item.subcontractor_checked ? 'bg-[#0891B2] border-[#0891B2]' : 'bg-white border-gray-200'}`}>
            {item.subcontractor_checked ? <i className="ri-check-line text-white text-[8px]"></i> : <span className="text-[8px] font-bold text-[#9CA3AF]">S</span>}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium leading-snug ${isFullyComplete ? 'text-[#9CA3AF] line-through' : 'text-[#374151]'}`}>
          {item.title}
        </p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: catCfg.bg, color: catCfg.text }}>
            {item.category}
          </span>
          {item.checklistTitle && <span className="text-[9px] text-[#9CA3AF]">{item.checklistTitle}</span>}
          {/* Separate status badges: Mgmt / Sub / Final */}
          {needsMgmt && (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${item.management_checked ? 'bg-[#F0FDF4] text-[#059669]' : 'bg-[#FEF0E7] text-[#E8621A]'}`}>
              {t('progress.management')}: {item.management_checked ? t('progress.done') : t('progress.pending')}
            </span>
          )}
          {needsMgmt && !needsSub && item.management_checked && (
            <span className="text-[9px] font-bold bg-[#F0FDF4] text-[#059669] px-1.5 py-0.5 rounded-full">{t('checklist.finalCompleteLabel')}</span>
          )}
          {needsSub && (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${item.subcontractor_checked ? 'bg-[#ECFEFF] text-[#0891B2]' : 'bg-[#FFFBEB] text-[#F59E0B]'}`}>
              {t('progress.subcontractor')}: {item.subcontractor_checked ? t('progress.done') : t('progress.pending')}
            </span>
          )}
          {needsSub && item.management_checked && !item.subcontractor_checked && (
            <span className="text-[9px] font-bold bg-[#FFFBEB] text-[#F59E0B] px-1.5 py-0.5 rounded-full">{t('checklist.waitingForSubLabel')}</span>
          )}
          {needsSub && !item.management_checked && (
            <span className="text-[9px] font-bold bg-[#FEF0E7] text-[#E8621A] px-1.5 py-0.5 rounded-full">{t('checklist.waitingForMgmtLabel')}</span>
          )}
          {isFullyComplete && (needsMgmt || needsSub) && (
            <span className="text-[9px] font-bold bg-[#F0FDF4] text-[#059669] px-1.5 py-0.5 rounded-full">{t('checklist.finalCompleteLabel')}</span>
          )}
          {!needsMgmt && !needsSub && (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${isFullyComplete ? 'bg-[#F0FDF4] text-[#059669]' : 'bg-[#FEF0E7] text-[#E8621A]'}`}>
              {isFullyComplete ? t('checklist.completedLabel') : t('checklist.requiredLabel')}
            </span>
          )}
          {item.confidence && item.confidence !== 'high' && (
            <span className="text-[9px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full">{item.confidence}</span>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-1 flex-shrink-0">
        {needsMgmt && (
          <button
            onClick={() => { if (isManagement && !isToggling) onToggleManagement(); }}
            disabled={isToggling || !isManagement}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap border-2 transition-colors disabled:opacity-40 ${
              item.management_checked
                ? 'bg-[#F0FDF4] border-[#BBF7D0] text-[#059669]'
                : 'bg-white border-[#E8621A] text-[#E8621A] hover:bg-[#FEF0E7]'
            }`}
            title={isManagement ? t('checklist.completeMgmtShort') : t('checklist.managementCheck')}
          >
            {isToggling ? (
              <i className="ri-loader-4-line animate-spin text-[8px]"></i>
            ) : item.management_checked ? (
              <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[8px]"></i> {t('checklist.managementDoneShort')}</span>
            ) : (
              <span className="flex items-center gap-1"><i className="ri-checkbox-blank-circle-line text-[8px]"></i> {t('checklist.completeMgmtShort')}</span>
            )}
          </button>
        )}
        {needsSub && (
          <button
            onClick={() => { if ((isManagement || isSubcontractor) && !isToggling) onToggleSubcontractor(); }}
            disabled={isToggling || !(isManagement || isSubcontractor)}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap border-2 transition-colors disabled:opacity-40 ${
              item.subcontractor_checked
                ? 'bg-[#ECFEFF] border-[#A5F3FC] text-[#0891B2]'
                : 'bg-white border-[#0891B2] text-[#0891B2] hover:bg-[#ECFEFF]'
            }`}
            title={(isManagement || isSubcontractor) ? t('checklist.completeSubShort') : t('checklist.subcontractorCheck')}
          >
            {isToggling ? (
              <i className="ri-loader-4-line animate-spin text-[8px]"></i>
            ) : item.subcontractor_checked ? (
              <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[8px]"></i> {t('checklist.subDoneShort')}</span>
            ) : (
              <span className="flex items-center gap-1"><i className="ri-checkbox-blank-circle-line text-[8px]"></i> {t('checklist.completeSubShort')}</span>
            )}
          </button>
        )}
      </div>

      {isManagement && onUnlink && (
        <button
          onClick={(e) => { e.stopPropagation(); onUnlink(); }}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#FEF2F2] text-[#D1D5DB] hover:text-[#EF4444] cursor-pointer flex-shrink-0 transition-colors"
          title={t('checklist.unmappedLabel')}
        >
          <i className="ri-close-circle-line text-xs"></i>
        </button>
      )}
      {isManagement && onRequestAction && !isFullyComplete && (
        <button
          onClick={(e) => { e.stopPropagation(); onRequestAction(item); }}
          className="px-2.5 py-1 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap border-2 border-[#059669] text-[#059669] hover:bg-[#F0FDF4] transition-colors flex-shrink-0"
          title={t('checklist.requestActionShort')}
        >
          <span className="flex items-center gap-1"><i className="ri-send-plane-line text-[8px]"></i> {t('checklist.requestActionShort')}</span>
        </button>
      )}
    </div>
  );
}

// formatShortDate is imported from @/lib/dateUtils above